import { ref, onMounted, onUnmounted } from 'vue'

/**
 * Centralized keyboard shortcuts composable.
 *
 * Registers a single global keydown listener and dispatches CustomEvents
 * (the same mechanism used by App.vue for cross-component communication).
 *
 * Shortcuts are only active when:
 *  - the user is NOT typing in an input/textarea/contenteditable
 *  - no modifier key is held (unless the shortcut explicitly requires one)
 *
 * SearchBar already owns the "/" shortcut internally, so we skip it here.
 */

// Shared state so the help dialog can read the registry
const showHelpDialog = ref(false)

/**
 * Check whether the currently focused element is an editable field.
 * Mirrors the same guard SearchBar uses for its "/" handler.
 */
function isEditableElementFocused() {
  const el = document.activeElement
  if (!el) return false

  const tag = el.tagName
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return true
  if (el.isContentEditable) return true
  if (el.getAttribute('contenteditable') === 'true') return true
  if (el.closest('[contenteditable="true"]')) return true
  if (el.closest('.ProseMirror')) return true
  if (el.closest('.tiptap')) return true

  return false
}

/**
 * The shortcut map: key → { description, category, handler }
 *
 * Groups:
 *  Navigation   — move between app sections
 *  Actions      — create / modify content
 *  View         — toggle UI panels
 *  General      — help, escape, etc.
 */
function buildShortcutMap() {
  return {
    // ── Actions ─────────────────────────────────────────
    n: {
      description: 'Add new bookmark',
      category: 'Actions',
      handler: () => window.dispatchEvent(new CustomEvent('open-add-bookmark-dialog')),
    },
    d: {
      description: 'Add new subscription',
      category: 'Actions',
      handler: () => window.dispatchEvent(new CustomEvent('open-add-subscription-dialog')),
    },
    f: {
      description: 'Focus search bar',
      category: 'Search',
      handler: () => window.dispatchEvent(new CustomEvent('focus-search')),
    },

    // ── View ────────────────────────────────────────────
    s: {
      description: 'Toggle sidebar',
      category: 'View',
      handler: () => window.dispatchEvent(new CustomEvent('toggle-sidebar')),
    },
    i: {
      description: 'Toggle inbox view',
      category: 'View',
      handler: () => window.dispatchEvent(new CustomEvent('toggle-inbox')),
    },
    x: {
      description: 'Toggle selection mode',
      category: 'View',
      handler: () => window.dispatchEvent(new CustomEvent('toggle-selection-mode')),
    },

    // ── Navigation ──────────────────────────────────────
    'g b': {
      description: 'Go to Bookmarks',
      category: 'Navigation',
      twoStep: true,
      handler: () => window.dispatchEvent(new CustomEvent('navigate', { detail: '/bookmarks' })),
    },
    'g f': {
      description: 'Go to Feeds',
      category: 'Navigation',
      twoStep: true,
      handler: () => window.dispatchEvent(new CustomEvent('navigate', { detail: '/feeds' })),
    },
    'g t': {
      description: 'Go to Tags',
      category: 'Navigation',
      twoStep: true,
      handler: () => window.dispatchEvent(new CustomEvent('navigate', { detail: '/tags' })),
    },
    'g r': {
      description: 'Go to Rules',
      category: 'Navigation',
      twoStep: true,
      handler: () => window.dispatchEvent(new CustomEvent('navigate', { detail: '/rules' })),
    },

    // ── General ─────────────────────────────────────────
    '?': {
      description: 'Show keyboard shortcuts',
      category: 'General',
      handler: () => { showHelpDialog.value = true },
    },
    Escape: {
      description: 'Close dialog / deselect',
      category: 'General',
      allowInEditable: true,
      handler: () => window.dispatchEvent(new CustomEvent('shortcut-escape')),
    },
  }
}

export function useKeyboardShortcuts() {
  const shortcuts = buildShortcutMap()

  // For two-step combos like "g b": track the first key pressed
  let pendingPrefix = null
  let pendingTimer = null
  const COMBO_TIMEOUT = 800 // ms to wait for second key

  function clearPending() {
    pendingPrefix = null
    if (pendingTimer) {
      clearTimeout(pendingTimer)
      pendingTimer = null
    }
  }

  function handleKeydown(event) {
    // Ignore when modifier keys are held (allow Escape regardless)
    if ((event.ctrlKey || event.metaKey || event.altKey) && event.key !== 'Escape') {
      clearPending()
      return
    }

    const key = event.key

    // ── Two-step combo handling ──────────────────────────
    if (pendingPrefix) {
      const combo = `${pendingPrefix} ${key}`
      const shortcut = shortcuts[combo]
      clearPending()

      if (shortcut) {
        event.preventDefault()
        shortcut.handler()
      }
      return
    }

    // Check for first key of a two-step combo (e.g. "g")
    const possibleCombos = Object.keys(shortcuts).filter(
      (k) => shortcuts[k].twoStep && k.startsWith(`${key} `)
    )
    if (possibleCombos.length > 0 && !isEditableElementFocused()) {
      pendingPrefix = key
      pendingTimer = setTimeout(clearPending, COMBO_TIMEOUT)
      return
    }

    // ── Single-key shortcuts ────────────────────────────
    const shortcut = shortcuts[key]
    if (!shortcut) return

    // Skip if the user is typing (unless shortcut allows it)
    if (!shortcut.allowInEditable && isEditableElementFocused()) return

    event.preventDefault()
    shortcut.handler()
  }

  onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
    clearPending()
  })

  return {
    showHelpDialog,
    shortcuts,
  }
}
